import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import Contact from '../models/Contact';
import { ContactOption } from '../models/enums/ContactOption';
import { updateContact } from '../services/contact-service';

interface EditContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    onError: (message: string) => void;
    onEditContact: (contact: Contact) => void;
    contact: Contact;
}

const EditContactModal: React.FC<EditContactModalProps> = ({
                                                               isOpen,
                                                               onClose,
                                                               onError,
                                                               onEditContact,
                                                               contact,
                                                           }) => {
    const maxNotes = 1000;
    const contactOptions = Object.values(ContactOption).map(method => ({
        value: method,
        label: method,
    }));

    const [name, setName] = useState<string>(contact.name);
    const [email, setEmail] = useState<string>(contact.email || '');
    const [phone, setPhone] = useState<string>(contact.phone || '');
    const [notes, setNotes] = useState<string>(contact.notes || '');
    const [preferredContact, setPreferredContact] = useState<ContactOption>(
        contact.preferredContact || contactOptions[0].value
    );
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen) {
            setName(contact.name);
            setEmail(contact.email || '');
            setPhone(contact.phone || '');
            setNotes(contact.notes || '');
            setPreferredContact(contact.preferredContact || contactOptions[0].value);
            setErrors({});
        }
    }, [isOpen, contact]);

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!name.trim()) errs.name = 'Name is required';
        if (name.length > 100) errs.name = 'Name must be 100 characters or fewer';
        if (email && !/.+@.+\..+/.test(email)) errs.email = 'Invalid email';
        if (email && email.length > 100) errs.email = 'Email must be 100 characters or fewer';
        if (phone && !/^\+?[0-9]{7,15}$/.test(phone))
            errs.phone = 'Invalid phone number (do not use dashes)';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        setLoading(true);
        if (!validate()) {
            setLoading(false);
            return;
        }

        const payload: Contact = {
            id: contact.id,
            name: name.trim(),
            preferredContact: preferredContact,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
            notes: notes.trim() || undefined,
            clientId: contact.clientId,
            userId: contact.userId || undefined,
        };

        try {
            const response = await updateContact(payload);
            if (response.status === 200 && response.data) {
                onEditContact(response.data);
            } else {
                onError('Unable to update contact. Please try again later');
            }
        } catch {
            onError('Unable to update contact. Please try again later');
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />
            <div className="relative bg-white w-full max-w-md p-6 rounded-lg shadow-lg overflow-auto max-h-screen">
                <Dialog.Title className="text-xl font-semibold mb-4">Edit Contact</Dialog.Title>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="contact-name" className="block font-medium">Name *</label>
                        <input
                            id="contact-name"
                            type="text"
                            className="w-full border rounded p-2"
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                        {errors.name && <p data-testid="name-error" className="text-red-600 text-sm">{errors.name}</p>}
                    </div>

                    <div>
                        <label htmlFor="contact-email" className="block font-medium">Email</label>
                        <input
                            id="contact-email"
                            type="email"
                            className="w-full border rounded p-2"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                        {errors.email && <p data-testid="email-error" className="text-red-600 text-sm">{errors.email}</p>}
                    </div>

                    <div>
                        <label htmlFor="contact-phone" className="block font-medium">Phone</label>
                        <input
                            id="contact-phone"
                            type="tel"
                            className="w-full border rounded p-2"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                        />
                        {errors.phone && <p className="text-red-600 text-sm">{errors.phone}</p>}
                    </div>

                    <div>
                        <label htmlFor="contact-notes" className="block font-medium">Notes</label>
                        <textarea
                            id="contact-notes"
                            rows={3}
                            maxLength={maxNotes}
                            className="w-full border rounded p-2"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                        />
                        <p className="text-gray-500 text-sm mt-1">{notes.length}/{maxNotes} characters</p>
                    </div>

                    <div>
                        <label htmlFor="preferred-contact" className="block font-medium">Preferred Contact *</label>
                        <select
                            id="preferred-contact"
                            className="w-full border px-3 py-2 rounded"
                            value={preferredContact}
                            onChange={e => setPreferredContact(e.target.value as ContactOption)}
                        >
                            {contactOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                        <button onClick={onClose} className="px-4 py-2 rounded border">Cancel</button>
                        <button
                            onClick={handleSubmit}
                            className="px-4 py-2 rounded bg-crumbblue text-white"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default EditContactModal;